import { useState } from 'react'
import { Brain, Sparkles, Zap, Globe, MessageSquare } from 'lucide-react'

interface AIFeature {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  example: string
  status: 'active' | 'loading' | 'error'
}

function AIFeaturesPage() {
  const [selectedFeature, setSelectedFeature] = useState<string>('')
  const [testResults, setTestResults] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState<Record<string, boolean>>({})

  const aiFeatures: AIFeature[] = [
    {
      id: 'world-events',
      name: 'AI-Generated World Events',
      description: 'Dynamic events generated based on your country\'s state, global conditions, and historical patterns.',
      icon: <Globe className="w-6 h-6" />,
      example: 'A trade dispute emerges with neighboring countries due to your recent tax policies...',
      status: 'active'
    },
    {
      id: 'news-headlines',
      name: 'Dynamic News Headlines',
      description: 'Realistic news headlines generated for every major event in your country.',
      icon: <MessageSquare className="w-6 h-6" />,
      example: 'Breaking: Economic Reforms Spark Mixed Reactions in Capital',
      status: 'active'
    },
    {
      id: 'opposition-ai',
      name: 'Intelligent Opposition',
      description: 'AI-powered opposition that responds realistically to your policies and creates dynamic political challenges.',
      icon: <Brain className="w-6 h-6" />,
      example: 'Opposition Leader: "This budget fails to address the real concerns of working families..."',
      status: 'active'
    },
    {
      id: 'personalized-events',
      name: 'Personalized Events',
      description: 'Events tailored to your governing style and past decisions, creating unique storylines.',
      icon: <Sparkles className="w-6 h-6" />,
      example: 'Your previous environmental policies have attracted international attention...',
      status: 'active'
    },
    {
      id: 'diplomatic-dialogue',
      name: 'Dynamic Diplomacy',
      description: 'AI-generated diplomatic communications between countries based on current relationships and events.',
      icon: <Zap className="w-6 h-6" />,
      example: 'The Ambassador stated: "We view these trade measures as an opportunity for deeper cooperation..."',
      status: 'active'
    }
  ]

  const testAIFeature = async (featureId: string) => {
    setLoading(prev => ({ ...prev, [featureId]: true }))
    
    try {
      let response
      
      switch (featureId) {
        case 'world-events':
          response = await fetch('/api/ai/world-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gameState: {
                country: { 
                  name: 'Democracia', 
                  governmentType: 'Democracy',
                  population: 50000000,
                  gdp: 800 
                },
                currentTurn: 10,
                budget: 150,
                approval: 65,
                recentEvents: []
              },
              eventType: 'economic',
              severity: 'moderate'
            })
          })
          break
          
        case 'news-headlines':
          response = await fetch('/api/ai/news-headline', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: { title: 'New Economic Policy Announced' },
              country: { name: 'Democracia' }
            })
          })
          break
          
        case 'opposition-ai':
          response = await fetch('/api/ai/opposition-response', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              policy: 'increased corporate taxes',
              country: { name: 'Democracia', governmentType: 'Democracy' },
              approval: 65
            })
          })
          break
          
        case 'personalized-events':
          response = await fetch('/api/ai/personalized-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              game: {
                id: 'test-game',
                currentTurn: 10,
                country: { 
                  name: 'Democracia',
                  treasury: 150,
                  happiness: 65
                }
              },
              decisionHistory: [
                { decision: 'Increased education spending', outcome: 'Public approval increased' },
                { decision: 'Reduced military budget', outcome: 'Defense contractors unhappy' }
              ]
            })
          })
          break
          
        case 'diplomatic-dialogue':
          response = await fetch('/api/ai/diplomatic-dialogue', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              country1: { name: 'Democracia', governmentType: 'Democracy' },
              country2: { name: 'Neighboria', governmentType: 'Republic' },
              eventType: 'trade agreement'
            })
          })
          break
          
        default:
          throw new Error('Unknown feature')
      }
      
      if (!response) throw new Error('No response received')
      
      const data = await response.json()
      setTestResults(prev => ({ ...prev, [featureId]: data }))
      
    } catch (error) {
      console.error(`Error testing ${featureId}:`, error)
      setTestResults(prev => ({ 
        ...prev, 
        [featureId]: { 
          error: 'AI service unavailable in development mode',
          note: 'AI features require Cloudflare AI binding in production'
        }
      }))
    } finally {
      setLoading(prev => ({ ...prev, [featureId]: false }))
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🤖 AI-Powered Government Simulation
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Experience next-generation government simulation powered by Cloudflare AI. 
          Dynamic events, intelligent opposition, and personalized storytelling.
        </p>
      </div>

      {/* AI Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {aiFeatures.map((feature) => (
          <div 
            key={feature.id}
            className={`card hover:shadow-lg transition-all duration-300 cursor-pointer ${
              selectedFeature === feature.id ? 'ring-2 ring-primary-500' : ''
            }`}
            onClick={() => setSelectedFeature(selectedFeature === feature.id ? '' : feature.id)}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-primary-100 rounded-lg text-primary-600">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{feature.name}</h3>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">{feature.description}</p>
            
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Example:</p>
              <p className="text-sm text-gray-700 italic">"{feature.example}"</p>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation()
                testAIFeature(feature.id)
              }}
              disabled={loading[feature.id]}
              className="w-full btn btn-primary text-sm"
            >
              {loading[feature.id] ? 'Testing...' : 'Test AI Feature'}
            </button>

            {testResults[feature.id] && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium mb-2">AI Response:</p>
                <div className="text-sm text-blue-800">
                  {testResults[feature.id].error ? (
                    <div className="text-red-600">
                      <p className="font-medium">{testResults[feature.id].error}</p>
                      {testResults[feature.id].note && (
                        <p className="text-xs mt-1">{testResults[feature.id].note}</p>
                      )}
                    </div>
                  ) : (
                    <pre className="whitespace-pre-wrap text-xs bg-white p-2 rounded border">
                      {JSON.stringify(testResults[feature.id], null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* AI Technology Stack */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">🧠 AI Technology Stack</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Cloudflare AI Models</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span><strong>@cf/meta/llama-2-7b-chat-int8</strong> - Text generation for events and dialogue</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span><strong>Edge computing</strong> - Low latency AI responses worldwide</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span><strong>Serverless scaling</strong> - Automatic scaling based on demand</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">AI Features</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Dynamic event generation based on game state</li>
              <li>• Personalized content based on player decisions</li>
              <li>• Realistic political and economic scenarios</li>
              <li>• Adaptive difficulty and challenge scaling</li>
              <li>• Multi-language support (coming soon)</li>
              <li>• Historical scenario recreation</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Development Status */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">🚀 Development Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-green-600 font-bold text-lg">✓</span>
            </div>
            <p className="font-medium text-gray-900">AI Service Integration</p>
            <p className="text-sm text-gray-600">Cloudflare AI binding configured</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-yellow-600 font-bold text-lg">⚡</span>
            </div>
            <p className="font-medium text-gray-900">Event Generation</p>
            <p className="text-sm text-gray-600">Dynamic world events active</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-blue-600 font-bold text-lg">🔄</span>
            </div>
            <p className="font-medium text-gray-900">Continuous Learning</p>
            <p className="text-sm text-gray-600">AI adapts to player behavior</p>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-white rounded-lg border-l-4 border-blue-500">
          <p className="text-sm text-gray-700">
            <strong>Note:</strong> AI features require Cloudflare AI binding in production. 
            In development mode, you'll see fallback responses or mock data.
          </p>
        </div>
      </div>
    </div>
  )
}

export default AIFeaturesPage